import { Box, Stack, styled, useTheme } from '@mui/material'
import {
    assembleNameWithEmailFallback,
    ConfirmationDialog,
    ContentChip,
    DisplayUserLink,
    getLocalizedDateString,
    isoDateStringToLocalizedTime,
    OptionsMenu,
    Typography,
    User,
} from 'ComponentLibrary'

import { createElement, Fragment, isValidElement, ReactNode, ReactPortal, useState } from 'react'
import { mentionPattern, TextEditor } from 'ComponentLibrary/src/molecules/TextEditor/TextEditor'
import Markdown, { Components } from 'react-markdown'
import { AttachmentPreview } from 'ComponentLibrary/src/molecules/TextEditor/AttachmentPreview'
import {
    ExistingExternalParticipantMessage,
    ExistingMessage,
    ExistingUserMessage,
    SystemMessage,
} from 'ComponentLibrary/src/molecules/Message/types'
import { PencilSimple, Trash, UserCircleGearIcon } from '@phosphor-icons/react'
import type { ExternalParticipantDisplayData } from 'ComponentLibrary/src/common/userPresentations'
import type { UserDisplayData } from 'ComponentLibrary/src/common/users'
import { getDefaultUserLinkCopy, DEFAULT_USER_LINK_COPY, type UserLinkCopy } from 'ComponentLibrary/src/piazza/text/UserLink/UserLink'
import { type TextEditorCopy } from 'ComponentLibrary/src/molecules/TextEditor/TextEditor'
import { useTranslation } from 'react-i18next'

type MessageComponentProps = {
    message: ExistingMessage | SystemMessage
    currentUserId?: number
    deleteMessage: (messageId: string) => void
    updateMessage: (messageId: string, content: string, mentions: number[]) => void
    isAggregated?: boolean
    downloadDocument: (messageId: string, documentIndex: number) => void
    mentionOptions?: User[]
    externalParticipantsUsers?: ExternalParticipantDisplayData[]
    copy?: Partial<MessageCopy>
    locale?: string
}

export type MessageCopy = {
    deleteTitle: string
    deleteDialogContent: string
    deleteConfirm: string
    edit: string
    delete: string
    edited: string
    attachments: string
    deletedMessage: string
    system: string
    textEditor: TextEditorCopy
    userLink: UserLinkCopy
}

const DEFAULT_MESSAGE_COPY: MessageCopy = {
    deleteTitle: 'Nachricht löschen',
    deleteDialogContent: 'Möchten Sie die Nachricht endgültig löschen?',
    deleteConfirm: 'Endgültig löschen',
    edit: 'Nachricht bearbeiten',
    delete: 'Nachricht löschen',
    edited: 'bearbeitet',
    attachments: 'Anhänge',
    deletedMessage: 'Nachricht wurde gelöscht',
    system: 'System',
    textEditor: {
        noMentionResults: 'Keine Ergebnisse',
        placeholder: 'Neue Nachricht',
        attachmentTooLarge: 'Der Anhang ist größer als 10MB.',
        toolbar: {
            textStyle: 'Textstil',
            bold: 'Fett',
            italic: 'Kursiv',
            bulletedList: 'Aufzählung',
            numberedList: 'Nummerierte Liste',
            headingOptions: {
                paragraph: 'Text',
                h2: 'Headline 1',
                h3: 'Headline 2',
                h4: 'Headline 3',
                h5: 'Headline 4',
            },
        },
    },
    userLink: DEFAULT_USER_LINK_COPY,
}

type StandardMessageProps = {
    message: ExistingMessage
    currentUserId?: number
    deleteMessage: (messageId: string) => void
    updateMessage: (messageId: string, content: string, mentions: number[]) => void
    isAggregated?: boolean
    downloadDocument: (messageId: string, documentIndex: number) => void
    userOptions?: User[]
    externalParticipantsUsers?: ExternalParticipantDisplayData[]
    copy: MessageCopy
    locale: string
}

type SystemMessageProps = {
    message: SystemMessage
    downloadDocument: (messageId: string, documentIndex: number) => void
    copy: MessageCopy
    locale: string
}

type MessageAuthorPresentation = {
    user: UserDisplayData
    isExternalUser: boolean
    isOwnMessage: boolean
}

let users: User[] = []

function resolveMessageAuthorPresentation({
    message,
    currentUserId,
    users,
    externalParticipantsUsers,
}: {
    message: ExistingMessage
    currentUserId?: number
    users: User[]
    externalParticipantsUsers: ExternalParticipantDisplayData[]
}): MessageAuthorPresentation {
    if (message.author_type === 'external_participant') {
        return resolveExternalParticipantAuthorPresentation({ message, externalParticipantsUsers })
    }

    return resolveInternalUserAuthorPresentation({ message, currentUserId, users })
}

function resolveExternalParticipantAuthorPresentation({
    message,
    externalParticipantsUsers,
}: {
    message: ExistingExternalParticipantMessage
    externalParticipantsUsers: ExternalParticipantDisplayData[]
}): MessageAuthorPresentation {
    const externalUser = externalParticipantsUsers.find(
        (externalParticipantUser) => externalParticipantUser.uuid === message.external_participant_id
    )

    if (!externalUser) {
        return {
            user: {
                first_name: message.user_name,
                organization_name: message.organization_name,
                email: '',
            },
            isExternalUser: true,
            isOwnMessage: false,
        }
    }

    return {
        user: {
            uuid: externalUser.uuid,
            first_name: externalUser.first_name,
            last_name: externalUser.last_name,
            organization_name: externalUser.organization_name || '',
            email: externalUser.email,
        },
        isExternalUser: true,
        isOwnMessage: false,
    }
}

function resolveInternalUserAuthorPresentation({
    message,
    currentUserId,
    users,
}: {
    message: ExistingUserMessage
    currentUserId?: number
    users: User[]
}): MessageAuthorPresentation {
    const user = users.find((option) => option.id === message.user_id)

    if (user) {
        return { user, isExternalUser: false, isOwnMessage: currentUserId === message.user_id }
    }

    return {
        user: {
            id: message.user_id,
            first_name: message.user_name,
            organization_name: message.organization_name,
            email: '',
        },
        isExternalUser: false,
        isOwnMessage: currentUserId === message.user_id,
    }
}

export function Message({
    message,
    deleteMessage,
    updateMessage,
    currentUserId,
    isAggregated,
    downloadDocument,
    mentionOptions,
    externalParticipantsUsers,
    copy,
    locale = 'de-DE',
}: MessageComponentProps) {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    mentionOptions && (users = mentionOptions)
    const resolvedCopy = { ...DEFAULT_MESSAGE_COPY, ...copy }
    const textEditorCopy = {
        ...DEFAULT_MESSAGE_COPY.textEditor,
        ...copy?.textEditor,
        toolbar: {
            ...DEFAULT_MESSAGE_COPY.textEditor.toolbar,
            ...copy?.textEditor?.toolbar,
            headingOptions: {
                ...DEFAULT_MESSAGE_COPY.textEditor.toolbar.headingOptions,
                ...copy?.textEditor?.toolbar?.headingOptions,
            },
        },
    }
    const userLinkCopy = { ...getDefaultUserLinkCopy(tCommon), ...copy?.userLink }
    const messageCopy = { ...resolvedCopy, textEditor: textEditorCopy, userLink: userLinkCopy }

    if (message.message_type === 'system_message') {
        return <SystemMessageComponent message={message} downloadDocument={downloadDocument} copy={messageCopy} locale={locale} />
    }
    if (message.message_type === 'existing_message') {
        return (
            <StandardMessage
                userOptions={mentionOptions}
                message={message}
                deleteMessage={deleteMessage}
                updateMessage={updateMessage}
                currentUserId={currentUserId}
                isAggregated={isAggregated}
                downloadDocument={downloadDocument}
                externalParticipantsUsers={externalParticipantsUsers}
                copy={messageCopy}
                locale={locale}
            />
        )
    }

    if (message.message_type === 'deleted_message') {
        return <DeletedMessage message={message} copy={messageCopy} locale={locale} />
    }

    return null
}

const Mention = ({ user_name }: { user_name: string }) => {
    return <ContentChip color="green" size="small" label={`@${user_name}`} />
}

function replaceMentionsWithComponent(text: string): ReactNode {
    mentionPattern.lastIndex = 0
    const parts = []
    let lastIndex = 0
    let match

    while ((match = mentionPattern.exec(text)) !== null) {
        const [fullMatch, id] = match
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index))
        }
        const mentionedUser = users.find((user) => user.id === Number.parseInt(id))
        const name = mentionedUser ? assembleNameWithEmailFallback(mentionedUser) : '?'
        parts.push(<Mention key={id} user_name={name} />)
        lastIndex = match.index + fullMatch.length
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex))
    }
    return <>{parts}</>
}

const normalizeLineEndings = (value: string) => value.replace(/\r\n?/g, '\n')

function transformChildren(children: ReactNode): ReactNode {
    if (typeof children === 'string') {
        return replaceMentionsWithComponent(children)
    }

    if (Array.isArray(children)) {
        const hasElementChildren = children.some((child) => isValidElement<ReactPortal>(child))

        return children.map((child, index) => {
            if (hasElementChildren && typeof child === 'string' && /^\s*\n\s*$/.test(child)) {
                return null
            }
            return <Fragment key={index}>{transformChildren(child)}</Fragment>
        })
    }

    if (isValidElement<ReactPortal>(children)) {
        const elementType = children.type
        const { children: innerChildren, ...otherProps } = children.props
        return createElement(elementType, otherProps, innerChildren && transformChildren(innerChildren))
    }

    return children
}

const renderers: Components = {
    p: ({ children, ...props }) => {
        return <p {...props}>{transformChildren(children)}</p>
    },
    li: ({ children, ...props }) => {
        return <li {...props}>{transformChildren(children)}</li>
    },
    h1: ({ children, ...props }) => {
        return <h1 {...props}>{transformChildren(children)}</h1>
    },
    h2: ({ children, ...props }) => {
        return <h2 {...props}>{transformChildren(children)}</h2>
    },
    h3: ({ children, ...props }) => {
        return <h3 {...props}>{transformChildren(children)}</h3>
    },
    h4: ({ children, ...props }) => {
        return <h4 {...props}>{transformChildren(children)}</h4>
    },
    h5: ({ children, ...props }) => {
        return <h5 {...props}>{transformChildren(children)}</h5>
    },
    strong: ({ children, ...props }) => {
        return <strong {...props}>{transformChildren(children)}</strong>
    },
    em: ({ children, ...props }) => {
        return <em {...props}>{transformChildren(children)}</em>
    },
    b: ({ children, ...props }) => {
        return <b {...props}>{transformChildren(children)}</b>
    },
    i: ({ children, ...props }) => {
        return <i {...props}>{transformChildren(children)}</i>
    },
}
const StandardMessage = ({
    message,
    deleteMessage,
    updateMessage,
    currentUserId,
    isAggregated,
    downloadDocument,
    userOptions,
    externalParticipantsUsers = [],
    copy,
    locale,
}: StandardMessageProps) => {
    const theme = useTheme()
    const { user, isExternalUser, isOwnMessage } = resolveMessageAuthorPresentation({
        message,
        currentUserId,
        users: userOptions || [],
        externalParticipantsUsers,
    })

    const [isEditing, setIsEditing] = useState<boolean>(false)
    const [isOpen, setIsOpen] = useState<boolean>(false)

    const onSubmit = (messageContent: string, mentions: number[]) => {
        updateMessage(message.uuid, messageContent, mentions)
        setIsEditing(false)
    }

    return (
        <Stack direction="column">
            <Stack direction="row" justifyContent="space-between">
                <Stack direction="row">
                    <ConfirmationDialog
                        open={isOpen}
                        onConfirm={() => {
                            setIsOpen(false)
                            deleteMessage(message.uuid)
                        }}
                        title={copy.deleteTitle}
                        dialogContent={copy.deleteDialogContent}
                        onCancel={() => setIsOpen(false)}
                        destructive
                        confirmLabel={copy.deleteConfirm}
                    />
                    {isAggregated ? (
                        <AggregatedStandardMessageContent message={message} locale={locale} />
                    ) : (
                        <DisplayUserLink user={user} isExternal={isExternalUser} copy={copy.userLink} />
                    )}
                </Stack>
                {isOwnMessage && (
                    <OptionsMenu
                        options={[
                            {
                                title: copy.edit,
                                callback: () => setIsEditing((prev) => !prev),
                                icon: <PencilSimple weight="bold" />,
                            },
                            {
                                title: copy.delete,
                                callback: () => setIsOpen(true),
                                danger: true,
                                icon: <Trash weight="bold" />,
                            },
                        ]}
                    />
                )}
            </Stack>
            {!isEditing && (
                <>
                    <StyledMarkdownContainer>
                        <Markdown components={renderers}>{normalizeLineEndings(message.content)}</Markdown>
                    </StyledMarkdownContainer>
                    {!isAggregated && (
                        <Typography variant="bodyTextXSmall" color={theme.palette.customtext.black54} style={{ marginLeft: '5px' }}>
                            {getLocalizedDateString(message.created_on, false, locale)}{' '}
                            {isoDateStringToLocalizedTime(message.created_on, locale)} {message.is_edited && ` (${copy.edited})`}
                        </Typography>
                    )}
                </>
            )}
            {isEditing && (
                <Box margin="12px 6px 12px 0">
                    <TextEditor
                        mentionOptions={userOptions}
                        onSubmit={({ message, mentions }) => onSubmit(message, mentions)}
                        onCancel={() => setIsEditing(false)}
                        initialMarkdownState={message.content}
                        submitVariant="toolbar"
                        enableHeadings
                        enableNumberedList
                        copy={copy.textEditor}
                    />
                </Box>
            )}
            <AttachmentPreview
                label={copy.attachments}
                documents={[...message.documents, ...message.images]} // Images are displayed for backwards compatibility
                downloadDocument={(index: number) => {
                    downloadDocument(message.uuid, index)
                }}
            />
        </Stack>
    )
}

function AggregatedStandardMessageContent({ message, locale }: { message: ExistingMessage; locale: string }) {
    const theme = useTheme()
    return (
        <>
            <Stack direction="column" sx={{ marginLeft: '5px', marginTop: '2px' }} justifyContent="center">
                <Typography variant="bodyTextXSmall" color={theme.palette.customtext.black54}>
                    {isoDateStringToLocalizedTime(message.created_on, locale)}
                </Typography>
            </Stack>
        </>
    )
}

const SystemMessageComponent = ({ message, downloadDocument, copy, locale }: SystemMessageProps) => (
    <Stack direction="column">
        <DisplayUserLink defaultIcon={<UserCircleGearIcon />} user={{ first_name: copy.system }} copy={copy.userLink} />
        <StyledMarkdownContainer>
            <Markdown components={renderers}>{normalizeLineEndings(message.content)}</Markdown>
        </StyledMarkdownContainer>
        <MessageCreatedTime createdOn={message.created_on} locale={locale} />
        <AttachmentPreview
            label={copy.attachments}
            documents={[...message.documents, ...message.images]} // Images are displayed for backwards compatibility
            downloadDocument={(index: number) => downloadDocument(message.uuid, index)}
        />
    </Stack>
)

const DeletedMessage = ({ message, copy, locale }: { message: ExistingMessage; copy: MessageCopy; locale: string }) => {
    const theme = useTheme()
    // Message deletion is disabled, so deleted messages only need the historical author fallback from the message itself.
    const { user, isExternalUser } = resolveMessageAuthorPresentation({
        message,
        users: [],
        externalParticipantsUsers: [],
    })

    return (
        <Stack direction="column" gap="6px">
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <DisplayUserLink inactive user={user} isExternal={isExternalUser} copy={copy.userLink} />
                <Typography
                    variant="bodyTextSmall"
                    style={{ marginRight: '10px', fontStyle: 'italic' }}
                    color={theme.palette.customtext.black54}
                >
                    {copy.deletedMessage}
                </Typography>
            </Stack>
            <MessageCreatedTime createdOn={message.created_on} locale={locale} />
        </Stack>
    )
}

const MessageCreatedTime = ({ createdOn, locale }: { createdOn: string; locale: string }) => {
    const theme = useTheme()
    return (
        <Typography variant="bodyTextXSmall" color={theme.palette.customtext.black54} style={{ marginLeft: '5px' }}>
            {getLocalizedDateString(createdOn, false, locale)} | {isoDateStringToLocalizedTime(createdOn, locale)}
        </Typography>
    )
}

const StyledMarkdownContainer = styled(Box)(() => ({
    fontSize: '14px',
    marginTop: '2px',
    marginLeft: '5px',
    '& p': {
        marginTop: '4px',
        marginBottom: '4px',
        whiteSpace: 'pre-line',
    },
    '& li > p': {
        marginTop: 0,
        marginBottom: 0,
        whiteSpace: 'pre-line',
    },
    '& h1, & h2, & h3, & h4, & h5': {
        marginTop: '4px',
        marginBottom: '4px',
        whiteSpace: 'pre-line',
    },
    '& ul, & ol': {
        marginTop: '4px',
        marginBottom: '4px',
        paddingInlineStart: '20px',
    },
    overflowWrap: 'break-word',
}))
